// src/hooks/usePosts.ts
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { queryKeys } from '../lib/queryClient';
import { ExtendedPost, Comment, PostFilters, PostsResponse } from '../types/post';

// Use ExtendedPost as the Post type in this hook
type Post = ExtendedPost;

interface FetchPostsParams {
  pageParam?: number;
  limit?: number;
  filters?: PostFilters;
}

// Fetch posts with pagination
const fetchPosts = async ({ pageParam = 0, limit = 20, filters = {} }: FetchPostsParams): Promise<PostsResponse> => {
  const params: Record<string, unknown> = {
    offset: pageParam,
    limit,
    ...filters,
  };

  // Remove undefined values
  Object.keys(params).forEach(key => {
    if (params[key] === undefined || params[key] === '') {
      delete params[key];
    }
  });

  const response = await api.get('/liturgical-posts/', { params });

  // Handle different response formats
  const data = response.data;
  const posts = Array.isArray(data) ? data : data.posts || data.items || [];
  const total = data.total || posts.length;

  return {
    posts,
    total,
    hasMore: posts.length === limit,
  };
};

// Infinite query hook for posts
export const useInfinitePosts = (filters?: PostFilters, limit = 20) => {
  return useInfiniteQuery({
    queryKey: queryKeys.posts.infinite(filters as Record<string, unknown> | undefined),
    queryFn: ({ pageParam = 0 }) => fetchPosts({ pageParam, limit, filters }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined;
      return allPages.reduce((acc, page) => acc + page.posts.length, 0);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Single post query
export const usePost = (postId: number | string) => {
  return useQuery({
    queryKey: queryKeys.posts.detail(postId),
    queryFn: async () => {
      const response = await api.get(`/liturgical-posts/${postId}`);
      return response.data as Post;
    },
    enabled: !!postId,
  });
};

// Comments for a post
export const usePostComments = (postId: number | string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.posts.comments(postId),
    queryFn: async () => {
      const response = await api.get(`/liturgical-posts/${postId}/comments`);
      return response.data as Comment[];
    },
    enabled: enabled && !!postId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Create post mutation
export const useCreatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (postData: FormData) => {
      const response = await api.post('/liturgical-posts/', postData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate posts cache to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
    },
  });
};

// Add comment mutation
export const useAddComment = (postId: number | string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      const response = await api.post(`/liturgical-posts/${postId}/comments`, { content });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate comments cache
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.comments(postId) });
      // Also update the post's comment count
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.detail(postId) });
    },
  });
};

// Like/Unlike post mutation
export const useToggleLike = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, isLiked }: { postId: number; isLiked: boolean }) => {
      if (isLiked) {
        await api.delete(`/liturgical-posts/${postId}/like`);
      } else {
        await api.post(`/liturgical-posts/${postId}/like`);
      }
      return { postId, isLiked: !isLiked };
    },
    onMutate: async ({ postId, isLiked }) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.posts.infinite() });

      // Get current data
      const previousData = queryClient.getQueryData(queryKeys.posts.infinite());

      // Optimistically update the cache
      queryClient.setQueryData(queryKeys.posts.infinite(), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            posts: page.posts.map((post: Post) =>
              post.id === postId
                ? {
                    ...post,
                    is_liked: !isLiked,
                    reactions_count: isLiked ? post.reactions_count - 1 : post.reactions_count + 1,
                  }
                : post
            ),
          })),
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.posts.infinite(), context.previousData);
      }
    },
  });
};

// Delete post mutation
export const useDeletePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (postId: number) => {
      await api.delete(`/liturgical-posts/${postId}`);
      return postId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
    },
  });
};

// Repost mutation
export const useRepost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (postId: number) => {
      const response = await api.post(`/liturgical-posts/${postId}/repost`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
    },
  });
};

export default useInfinitePosts;
